import { Box, Button, Card, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { showModal } from '../../../../app/reducers';
import { initialOtherIndividualInfo, OtherIndividualInfo } from '../../../../domain/person';
import { selectOtherIndividuals } from '../../reducers/other-individuals.reducer';
import { ApplicantInfoCard } from '../applicant-info-card/ApplicantInfoCard';
import { OtherIndividualCard } from '../other-individual-card';

type Props = {
  showApplicant: boolean;
  includePhone: boolean;
};

const OtherIndividuals: FC<Props> = ({ showApplicant, includePhone }) => {
  const individuals = useSelector(selectOtherIndividuals);
  const individualsDescOrder = useMemo(() => [...individuals].reverse(), [individuals]);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const cardVariant = isMobile ? undefined : 'outlined';

  const addIndividualHandler = useCallback(() => {
    dispatch(
      showModal({
        type: 'other-individual-info',
        context: {
          includePhone,
          edit: false,
          person: {
            ...initialOtherIndividualInfo,
            uiId: uuid(),
          },
        },
      }),
    );
  }, [dispatch, includePhone]);

  const editIndividualHandler = useCallback(
    (individual: OtherIndividualInfo) => {
      dispatch(
        showModal({
          type: 'other-individual-info',
          context: {
            includePhone,
            edit: true,
            person: individual,
          },
        }),
      );
    },
    [dispatch, includePhone],
  );

  return (
    <Stack spacing={4}>
      <Card variant={cardVariant}>
        <Box py={4} px={isMobile ? 0 : 4}>
          <Typography marginBottom={4} variant="subtitle1-medium">
            <>Owners and Executives</>
          </Typography>
          <Stack spacing={3}>
            <Typography variant="body2">
              Please add any individuals with a 25% or greater ownership in the business below.
            </Typography>
            <Typography variant="body2">
              If none of those individuals have significant responsibility to control, manage or direct the business
              (CEO, CFO, etc.), please also add an executive below.
            </Typography>
            <Typography variant="body2">
              To prevent funding of terrorism and money laundering activities, Federal law requires financial
              institutions to obtain and verify such information about each business that opens an account.
            </Typography>
          </Stack>
        </Box>
        <Stack spacing={2} mx={{ xs: 0, sm: 4 }}>
          {individualsDescOrder.map((individual) => (
            <OtherIndividualCard key={individual.uiId} individual={individual} handleEdit={editIndividualHandler} />
          ))}
          {showApplicant && <ApplicantInfoCard />}
        </Stack>
        <Box sx={{ mx: { xs: 0, sm: 4 }, mb: 4, mt: 2 }}>
          <Button variant="outlined" size="large" color="primary" fullWidth onClick={addIndividualHandler}>
            Add Owner or Executive
          </Button>
        </Box>
      </Card>
    </Stack>
  );
};

export { OtherIndividuals };
